<template>
  <div class="ai-chat-config-page">
    <b-card no-body>
      <template #header>
        <h4 class="mb-0">
          <feather-icon icon="CpuIcon" class="mr-75" />
          Configuração da IA
        </h4>
      </template>

      <b-tabs card>
        <!-- ══════════════ AGENTES ══════════════ -->
        <b-tab title="Agentes" active>
          <div class="d-flex justify-content-between mb-1">
            <p class="text-muted mb-0">
              Agentes definem a personalidade e o modelo usados pelo assistente. Apenas um pode ser o padrão.
              As regras técnicas e de segurança do sistema são sempre aplicadas pelo servidor.
            </p>
            <b-button variant="primary" size="sm" @click="abrirModal('agentes')">
              <feather-icon icon="PlusIcon" /> Novo agente
            </b-button>
          </div>
          <b-overlay :show="loading.agentes" rounded="sm">
            <b-table :items="dados.agentes" :fields="camposAgentes" show-empty small responsive
              empty-text="Nenhum agente cadastrado — o assistente usa o comportamento padrão do sistema.">
              <template #cell(ativo)="{ item }">
                <b-badge :variant="item.ativo ? 'light-success' : 'light-secondary'">{{ item.ativo ? 'Ativo' : 'Inativo' }}</b-badge>
              </template>
              <template #cell(padrao)="{ item }">
                <b-badge v-if="item.padrao" variant="light-primary">Padrão</b-badge>
              </template>
              <template #cell(acoes)="{ item }">
                <b-button variant="flat-primary" size="sm" @click="abrirModal('agentes', item)"><feather-icon icon="EditIcon" /></b-button>
                <b-button variant="flat-danger" size="sm" @click="excluir('agentes', item)"><feather-icon icon="TrashIcon" /></b-button>
              </template>
            </b-table>
          </b-overlay>
        </b-tab>

        <!-- ══════════════ BASE DE CONHECIMENTO ══════════════ -->
        <b-tab title="Base de conhecimento">
          <div class="d-flex justify-content-between mb-1">
            <p class="text-muted mb-0">
              Conteúdos de negócio/suporte que a IA usa para responder dúvidas. Podem ser globais ou de um agente específico.
            </p>
            <b-button variant="primary" size="sm" @click="abrirModal('base-conhecimento')">
              <feather-icon icon="PlusIcon" /> Nova entrada
            </b-button>
          </div>
          <b-overlay :show="loading['base-conhecimento']" rounded="sm">
            <b-table :items="dados['base-conhecimento']" :fields="camposConhecimento" show-empty small responsive
              empty-text="Nenhuma entrada cadastrada.">
              <template #cell(agente_id)="{ item }">{{ nomeAgente(item.agente_id) || 'Global' }}</template>
              <template #cell(ativo)="{ item }">
                <b-badge :variant="item.ativo ? 'light-success' : 'light-secondary'">{{ item.ativo ? 'Ativa' : 'Inativa' }}</b-badge>
              </template>
              <template #cell(acoes)="{ item }">
                <b-button variant="flat-primary" size="sm" @click="abrirModal('base-conhecimento', item)"><feather-icon icon="EditIcon" /></b-button>
                <b-button variant="flat-danger" size="sm" @click="excluir('base-conhecimento', item)"><feather-icon icon="TrashIcon" /></b-button>
              </template>
            </b-table>
          </b-overlay>
        </b-tab>

        <!-- ══════════════ ROTEAMENTO ══════════════ -->
        <b-tab title="Roteamento">
          <div class="d-flex justify-content-between mb-1">
            <p class="text-muted mb-0">
              Regras que escolhem o agente conforme o perfil do usuário e palavras-chave da mensagem.
              A primeira regra compatível (por ordem) vence; sem regra, vale o agente padrão.
            </p>
            <b-button variant="primary" size="sm" @click="abrirModal('roteamento')">
              <feather-icon icon="PlusIcon" /> Nova regra
            </b-button>
          </div>
          <b-overlay :show="loading.roteamento" rounded="sm">
            <b-table :items="dados.roteamento" :fields="camposRoteamento" show-empty small responsive
              empty-text="Nenhuma regra cadastrada.">
              <template #cell(agente_id)="{ item }">{{ nomeAgente(item.agente_id) || ('#' + item.agente_id) }}</template>
              <template #cell(perfil_acesso_id)="{ item }">{{ nomePerfil(item.perfil_acesso_id) || 'Todos' }}</template>
              <template #cell(ativo)="{ item }">
                <b-badge :variant="item.ativo ? 'light-success' : 'light-secondary'">{{ item.ativo ? 'Ativa' : 'Inativa' }}</b-badge>
              </template>
              <template #cell(acoes)="{ item }">
                <b-button variant="flat-primary" size="sm" @click="abrirModal('roteamento', item)"><feather-icon icon="EditIcon" /></b-button>
                <b-button variant="flat-danger" size="sm" @click="excluir('roteamento', item)"><feather-icon icon="TrashIcon" /></b-button>
              </template>
            </b-table>
          </b-overlay>
        </b-tab>

        <!-- ══════════════ WIDGET ══════════════ -->
        <b-tab title="Widget">
          <div class="d-flex justify-content-between mb-1">
            <p class="text-muted mb-0">
              Aparência e comportamento do chat flutuante. Somente uma configuração fica ativa por vez.
            </p>
            <b-button variant="primary" size="sm" @click="abrirModal('widget-config')">
              <feather-icon icon="PlusIcon" /> Nova configuração
            </b-button>
          </div>
          <b-overlay :show="loading['widget-config']" rounded="sm">
            <b-table :items="dados['widget-config']" :fields="camposWidget" show-empty small responsive
              empty-text="Nenhuma configuração — o widget usa o visual padrão.">
              <template #cell(cor_primaria)="{ item }">
                <span v-if="item.cor_primaria"><span class="cor-preview" :style="{ background: item.cor_primaria }"></span> {{ item.cor_primaria }}</span>
              </template>
              <template #cell(ativo)="{ item }">
                <b-badge :variant="item.ativo ? 'light-success' : 'light-secondary'">{{ item.ativo ? 'Ativa' : 'Inativa' }}</b-badge>
              </template>
              <template #cell(acoes)="{ item }">
                <b-button variant="flat-primary" size="sm" @click="abrirModal('widget-config', item)"><feather-icon icon="EditIcon" /></b-button>
                <b-button variant="flat-danger" size="sm" @click="excluir('widget-config', item)"><feather-icon icon="TrashIcon" /></b-button>
              </template>
            </b-table>
          </b-overlay>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- ══════════════ MODAL CRUD ══════════════ -->
    <b-modal v-model="modal.aberto" :title="tituloModal" size="lg" no-close-on-backdrop
      @ok.prevent="salvar" :ok-title="salvando ? 'Salvando...' : 'Salvar'" cancel-title="Cancelar" :ok-disabled="salvando">
      <template v-if="modal.recurso === 'agentes'">
        <b-form-group label="Nome *"><b-form-input v-model="form.nome" maxlength="120" /></b-form-group>
        <b-form-group label="Descrição"><b-form-input v-model="form.descricao" maxlength="500" /></b-form-group>
        <b-form-group label="Personalidade / escopo (system prompt)"
          description="Define tom e escopo do agente. As regras técnicas e de segurança do sistema continuam valendo sempre.">
          <b-form-textarea v-model="form.system_prompt" rows="6" />
        </b-form-group>
        <b-form-group label="Modelo">
          <b-form-select v-model="form.modelo" :options="meta.modelos" />
        </b-form-group>
        <b-form-checkbox v-model="form.ativo" switch class="mb-50">Ativo</b-form-checkbox>
        <b-form-checkbox v-model="form.padrao" switch>Agente padrão (usado quando nenhuma regra de roteamento casa)</b-form-checkbox>
      </template>

      <template v-else-if="modal.recurso === 'base-conhecimento'">
        <b-form-group label="Título *"><b-form-input v-model="form.titulo" maxlength="255" /></b-form-group>
        <b-form-group label="Conteúdo *"><b-form-textarea v-model="form.conteudo" rows="6" /></b-form-group>
        <b-form-group label="Palavras-chave" description="Separadas por vírgula. Ex.: frete, prazo de entrega, troca">
          <b-form-input v-model="form.palavras_chave" />
        </b-form-group>
        <b-form-group label="Agente (vazio = global do tenant)">
          <b-form-select v-model="form.agente_id" :options="opcoesAgentes" />
        </b-form-group>
        <b-form-group label="Ordem"><b-form-input v-model.number="form.ordem" type="number" /></b-form-group>
        <b-form-checkbox v-model="form.ativo" switch>Ativa</b-form-checkbox>
      </template>

      <template v-else-if="modal.recurso === 'roteamento'">
        <b-form-group label="Agente *">
          <b-form-select v-model="form.agente_id" :options="opcoesAgentes.slice(1)" />
        </b-form-group>
        <b-form-group label="Perfil de acesso (vazio = todos)">
          <b-form-select v-model="form.perfil_acesso_id" :options="opcoesPerfis" />
        </b-form-group>
        <b-form-group label="Departamento"><b-form-input v-model="form.departamento" maxlength="120" /></b-form-group>
        <b-form-group label="Palavras-chave" description="Separadas por vírgula. Vazio = casa com qualquer mensagem.">
          <b-form-input v-model="form.palavras_chave" />
        </b-form-group>
        <b-form-group label="Ordem"><b-form-input v-model.number="form.ordem" type="number" /></b-form-group>
        <b-form-checkbox v-model="form.ativo" switch>Ativa</b-form-checkbox>
      </template>

      <template v-else-if="modal.recurso === 'widget-config'">
        <b-form-group label="Nome *"><b-form-input v-model="form.nome" maxlength="120" /></b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="Cor primária">
              <b-input-group>
                <b-form-input v-model="form.cor_primaria" placeholder="#2563eb" maxlength="20" />
                <b-input-group-append>
                  <b-form-input v-model="form.cor_primaria" type="color" style="max-width: 48px" />
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Posição">
              <b-form-select v-model="form.posicao" :options="[{ value: 'bottom-right', text: 'Inferior direito' }, { value: 'bottom-left', text: 'Inferior esquerdo' }]" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="URL do avatar"><b-form-input v-model="form.avatar_url" maxlength="500" /></b-form-group>
        <b-form-group label="Mensagem de boas-vindas"><b-form-textarea v-model="form.mensagem_boas_vindas" rows="2" /></b-form-group>
        <b-form-group label="Mensagem de opt-in (vazio = sem opt-in)"><b-form-textarea v-model="form.mensagem_optin" rows="2" /></b-form-group>
        <b-form-group label="Ações rápidas (JSON)"
          description='Ex.: [{"label":"Criar pedido","mensagem":"Quero criar um pedido","perfis":["Representante"]}] — perfis vazio = todos.'>
          <b-form-textarea v-model="form.quick_actions" rows="4" />
        </b-form-group>
        <b-form-checkbox v-model="form.ativo" switch>Ativa (desativa as demais automaticamente)</b-form-checkbox>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios';

const FORM_VAZIO = {
  'agentes': { nome: '', descricao: '', system_prompt: '', modelo: 'gpt-4o', ativo: true, padrao: false },
  'base-conhecimento': { titulo: '', conteudo: '', palavras_chave: '', agente_id: null, ordem: 0, ativo: true },
  'roteamento': { agente_id: null, perfil_acesso_id: null, departamento: '', palavras_chave: '', ordem: 0, ativo: true },
  'widget-config': { nome: '', cor_primaria: '#2563eb', posicao: 'bottom-right', avatar_url: '', mensagem_boas_vindas: '', mensagem_optin: '', quick_actions: '', ativo: false },
};

export default {
  name: 'AiChatConfig',
  data() {
    return {
      dados: { 'agentes': [], 'base-conhecimento': [], 'roteamento': [], 'widget-config': [] },
      loading: { 'agentes': false, 'base-conhecimento': false, 'roteamento': false, 'widget-config': false },
      meta: { modelos: ['gpt-4o'], posicoes: [] },
      perfis: [],
      modal: { aberto: false, recurso: null, id: null },
      form: {},
      salvando: false,
      camposAgentes: [
        { key: 'nome', label: 'Nome' }, { key: 'modelo', label: 'Modelo' },
        { key: 'ativo', label: 'Status' }, { key: 'padrao', label: '' },
        { key: 'acoes', label: '', tdClass: 'text-right' },
      ],
      camposConhecimento: [
        { key: 'titulo', label: 'Título' }, { key: 'agente_id', label: 'Agente' },
        { key: 'ordem', label: 'Ordem' }, { key: 'ativo', label: 'Status' },
        { key: 'acoes', label: '', tdClass: 'text-right' },
      ],
      camposRoteamento: [
        { key: 'ordem', label: 'Ordem' }, { key: 'agente_id', label: 'Agente' },
        { key: 'perfil_acesso_id', label: 'Perfil' }, { key: 'palavras_chave', label: 'Palavras-chave' },
        { key: 'ativo', label: 'Status' }, { key: 'acoes', label: '', tdClass: 'text-right' },
      ],
      camposWidget: [
        { key: 'nome', label: 'Nome' }, { key: 'cor_primaria', label: 'Cor' },
        { key: 'posicao', label: 'Posição' }, { key: 'ativo', label: 'Status' },
        { key: 'acoes', label: '', tdClass: 'text-right' },
      ],
    };
  },
  computed: {
    tituloModal() {
      const nomes = { 'agentes': 'Agente', 'base-conhecimento': 'Entrada da base de conhecimento', 'roteamento': 'Regra de roteamento', 'widget-config': 'Configuração do widget' };
      return `${this.modal.id ? 'Editar' : 'Novo(a)'} ${nomes[this.modal.recurso] || ''}`;
    },
    opcoesAgentes() {
      return [{ value: null, text: 'Global (todos os agentes)' }]
        .concat(this.dados.agentes.map((a) => ({ value: a.id, text: a.nome })));
    },
    opcoesPerfis() {
      return [{ value: null, text: 'Todos os perfis' }]
        .concat(this.perfis.map((p) => ({ value: p.id, text: p.perfil_acesso_nome || p.nome })));
    },
  },
  created() {
    this.carregarTudo();
  },
  methods: {
    async carregarTudo() {
      await Promise.all([
        ...Object.keys(this.dados).map((r) => this.carregar(r)),
        this.carregarMeta(),
        this.carregarPerfis(),
      ]);
    },
    async carregar(recurso) {
      this.loading[recurso] = true;
      try {
        const { data } = await axios.get(`/ai-chat-admin/${recurso}`);
        this.dados[recurso] = data?.data || [];
      } catch (e) {
        this.toast('danger', e?.response?.data?.message || `Erro ao carregar ${recurso}`);
      } finally {
        this.loading[recurso] = false;
      }
    },
    async carregarMeta() {
      try {
        const { data } = await axios.get('/ai-chat-admin/meta');
        if (data?.data?.modelos) this.meta = data.data;
      } catch (e) { /* usa defaults */ }
    },
    async carregarPerfis() {
      try {
        const { data } = await axios.get('/perfilAcesso/findAll');
        const lista = data?.data || data || [];
        this.perfis = Array.isArray(lista) ? lista : [];
      } catch (e) { this.perfis = []; }
    },
    nomeAgente(id) {
      if (!id) return null;
      const a = this.dados.agentes.find((x) => x.id === id);
      return a ? a.nome : null;
    },
    nomePerfil(id) {
      if (!id) return null;
      const p = this.perfis.find((x) => Number(x.id) === Number(id));
      return p ? (p.perfil_acesso_nome || p.nome) : `#${id}`;
    },
    kwParaTexto(raw) {
      if (!raw) return '';
      try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr.join(', ') : String(raw); }
      catch (e) { return String(raw); }
    },
    abrirModal(recurso, item = null) {
      this.modal = { aberto: true, recurso, id: item?.id || null };
      if (item) {
        this.form = {
          ...item,
          ativo: !!item.ativo,
          padrao: !!item.padrao,
          palavras_chave: this.kwParaTexto(item.palavras_chave),
          quick_actions: item.quick_actions ? this.formatarJson(item.quick_actions) : '',
        };
      } else {
        this.form = { ...FORM_VAZIO[recurso] };
      }
    },
    formatarJson(raw) {
      try { return JSON.stringify(JSON.parse(raw), null, 2); } catch (e) { return String(raw); }
    },
    async salvar() {
      const r = this.modal.recurso;
      const payload = {};
      Object.keys(FORM_VAZIO[r]).forEach((k) => { payload[k] = this.form[k]; });
      if (payload.quick_actions === '') payload.quick_actions = null;
      this.salvando = true;
      try {
        if (this.modal.id) await axios.put(`/ai-chat-admin/${r}/${this.modal.id}`, payload);
        else await axios.post(`/ai-chat-admin/${r}`, payload);
        this.modal.aberto = false;
        this.toast('success', 'Salvo com sucesso');
        await this.carregar(r);
        if (r === 'agentes') await Promise.all([this.carregar('roteamento'), this.carregar('base-conhecimento')]);
      } catch (e) {
        this.toast('danger', e?.response?.data?.message || 'Erro ao salvar');
      } finally {
        this.salvando = false;
      }
    },
    async excluir(recurso, item) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover "${item.nome || item.titulo || `#${item.id}`}"?`, {
        title: 'Confirmar exclusão', okVariant: 'danger', okTitle: 'Remover', cancelTitle: 'Cancelar',
      });
      if (!ok) return;
      try {
        await axios.delete(`/ai-chat-admin/${recurso}/${item.id}`);
        this.toast('success', 'Removido');
        await this.carregar(recurso);
      } catch (e) {
        this.toast('danger', e?.response?.data?.message || 'Erro ao remover');
      }
    },
    toast(variant, texto) {
      this.$bvToast.toast(texto, { title: 'Configuração da IA', variant, solid: true });
    },
  },
};
</script>

<style scoped>
.cor-preview {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  vertical-align: middle;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
</style>
